import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Plus, Trash2, MessageCircle, KeyRound } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { PageTitle, AdminButton, useList } from "@/components/admin/kit";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { KnowledgeDocs } from "@/components/admin/KnowledgeDocs";

const IDEAS = ["What are the opening hours?", "Is there parking?", "How do I book a visit?", "What's included in the price?", "Who do I contact for more details?"];
const T = { item: "tour", items: "tours", who: "visitors" };

function Usage({ a }) {
  const max = Math.max(1, ...a.by_tour.map((r) => r.questions));
  return (
    <div className="rounded-3xl bg-white border p-6" data-testid="tdv-ai-usage">
      <div className="flex items-center gap-3"><MessageCircle className="w-5 h-5 text-slate-500" /><p className="font-semibold flex-1">AI chats this month</p>
        <p className="font-serif text-2xl" data-testid="tdv-ai-usage-count">{a.used}<span className="text-base text-slate-500">{a.limit ? ` / ${a.limit.toLocaleString()}` : " · no limit"}</span></p></div>
      <p className="text-xs text-slate-500 mt-2 mb-4">One chat = one visitor question. Resets on the 1st.</p>
      {a.by_tour.length === 0 && <p className="text-sm text-slate-500">No questions yet this month.</p>}
      <div className="space-y-3">{a.by_tour.map((r, i) => (
        <div key={r.tour_id} data-testid={`tdv-ai-usage-tour-${i}`}>
          <div className="flex justify-between text-sm"><span className="truncate pr-2">{r.name}</span><span className="font-semibold tabular-nums">{r.questions}</span></div>
          <div className="h-1.5 rounded-full bg-slate-100 mt-1 overflow-hidden"><div className="h-full bg-slate-900" style={{ width: `${(r.questions / max) * 100}%` }} /></div>
        </div>
      ))}</div>
    </div>
  );
}

function KeyCard({ a, setA }) {
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/tdv/openai-key", { key }); setA({ ...a, ...data }); setKey(""); toast.success("Key checked and saved. Your chats are now unlimited."); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const remove = async () => {
    if (!window.confirm("Remove your OpenAI key? The assistant goes back to the included monthly chats.")) return;
    const { data } = await api.delete("/tdv/openai-key"); setA({ ...a, ...data }); toast.success("Key removed");
  };
  return (
    <div className="rounded-3xl bg-white border p-6 space-y-3" data-testid="tdv-openai-key-card">
      <div className="flex items-center gap-3"><KeyRound className="w-5 h-5 text-slate-500" /><p className="font-semibold flex-1">Your own OpenAI key</p></div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className={`rounded-2xl border p-3 ${!a.own_key ? "ring-2 ring-slate-900" : ""}`} data-testid="tdv-ai-option-included"><p className="font-semibold text-sm">Included AI</p><p className="text-slate-500 mt-1">{a.plan_chats ? `${a.plan_chats.toLocaleString()} chats a month on us` : "Chats on us"}</p></div>
        <div className={`rounded-2xl border p-3 ${a.own_key ? "ring-2 ring-slate-900" : ""}`} data-testid="tdv-ai-option-own"><p className="font-semibold text-sm">Own key</p><p className="text-slate-500 mt-1">Unlimited chats, billed by OpenAI to you</p></div>
      </div>
      {a.own_key ? (
        <div className="flex items-center gap-2 rounded-xl bg-emerald-50 text-emerald-800 px-3 py-2 text-sm" data-testid="tdv-openai-key-saved"><Check className="w-4 h-4" /><span className="flex-1">Key ending {a.key_last4} is in use</span><button onClick={remove} className="text-rose-600 text-xs font-semibold" data-testid="tdv-openai-key-remove">Remove</button></div>
      ) : (
        <>
          <p className="text-xs text-slate-500">Paste a secret key from platform.openai.com → API keys. We check it works, store it encrypted and only show its last 4 characters.</p>
          <div className="flex gap-2"><Input type="password" autoComplete="off" value={key} onChange={(e) => setKey(e.target.value)} placeholder="sk-..." data-testid="tdv-openai-key-input" />
            <AdminButton onClick={save} disabled={busy || key.trim().length < 20} data-testid="tdv-openai-key-save">{busy ? "Checking…" : "Save"}</AdminButton></div>
        </>
      )}
    </div>
  );
}

function FaqRows({ faqs, setFaqs }) {
  const upd = (i, k, v) => setFaqs(faqs.map((f, j) => (j === i ? { ...f, [k]: v } : f)));
  return (
    <>
      {faqs.length === 0 && <p className="text-sm text-slate-500">No FAQs yet. Start with one of the ideas on the right.</p>}
      {faqs.map((f, i) => (
        <div key={i} className="rounded-2xl border p-4 space-y-2" data-testid={`tdv-faq-row-${i}`}>
          <div className="flex gap-2"><Input value={f.q} maxLength={200} onChange={(e) => upd(i, "q", e.target.value)} placeholder={`Question, e.g. ${IDEAS[0]}`} data-testid={`tdv-faq-q-${i}`} />
            <button onClick={() => setFaqs(faqs.filter((_, j) => j !== i))} className="w-10 h-10 shrink-0 rounded-lg text-rose-600 hover:bg-rose-50 grid place-items-center" data-testid={`tdv-faq-delete-${i}`}><Trash2 className="w-4 h-4" /></button></div>
          <Textarea rows={3} maxLength={1500} value={f.a} onChange={(e) => upd(i, "a", e.target.value)} placeholder="Answer" data-testid={`tdv-faq-a-${i}`} />
        </div>
      ))}
    </>
  );
}

function Suggestions({ list, set }) {
  return (
    <div className="rounded-3xl bg-white border p-6 space-y-2" data-testid="tdv-chat-suggestions">
      <p className="font-semibold">Suggested questions</p>
      <p className="text-xs text-slate-500">Shown as tap-to-ask buttons in the chat (up to 8). Click Save to keep them.</p>
      {list.map((q, i) => (
        <div key={i} className="flex gap-2"><Input value={q} maxLength={120} onChange={(e) => set(list.map((x, j) => (j === i ? e.target.value : x)))} data-testid={`tdv-suggestion-${i}`} />
          <button onClick={() => set(list.filter((_, j) => j !== i))} className="w-10 h-10 shrink-0 rounded-lg text-rose-600 hover:bg-rose-50 grid place-items-center" data-testid={`tdv-suggestion-delete-${i}`}><Trash2 className="w-4 h-4" /></button></div>
      ))}
      {list.length < 8 && <AdminButton variant="ghost" onClick={() => set([...list, ""])} data-testid="tdv-suggestion-add"><Plus className="w-4 h-4" />Add suggested question</AdminButton>}
    </div>
  );
}

export default function TdvAssistant() {
  const [a, setA] = useState(null);
  const [tours, reloadTours] = useList("/admin/tdv/tours");
  const [target, setTarget] = useState("");
  const tour = (tours || []).find((t) => t.id === target);
  const [tf, setTf] = useState([]);
  useEffect(() => { setTf(tour?.faqs || []); }, [tour]);
  useEffect(() => { api.get("/tdv/assistant").then((r) => setA(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!a) return null;
  const faqs = tour ? tf : a.faqs;
  const setFaqs = (v) => (tour ? setTf(v) : setA({ ...a, faqs: v }));
  const save = async () => {
    try {
      if (tour) await api.put(`/tdv/tours/${tour.id}/faqs`, { faqs: tf });
      const { data } = await api.put("/tdv/assistant", { faqs: a.faqs, chat_suggestions: a.chat_suggestions });
      setA(data); reloadTours(); toast.success("Saved. The assistant uses it straight away.");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="tdv-assistant-page">
      <PageTitle title="AI assistant" sub="The assistant answers visitors using each tour's details, hotspots, your FAQs and any documents you upload. It never makes things up." right={<AdminButton onClick={save} data-testid="tdv-faq-save"><Check className="w-4 h-4" />Save</AdminButton>} />
      <div className="grid lg:grid-cols-[1fr_340px] gap-6 items-start">
        <div className="space-y-6">
          <div className="rounded-3xl bg-white border p-6 space-y-4" data-testid="tdv-faq-list">
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[200px]"><p className="font-semibold">{tour ? `FAQs for ${tour.name} only` : "FAQs for all tours"}</p><p className="text-xs text-slate-500">{tour ? "For things that only apply to this tour. They win over account-wide answers." : "Write answers the way you'd want your team to say them."}</p></div>
              <select value={target} onChange={(e) => setTarget(e.target.value)} className="h-10 rounded-xl border px-3 text-sm bg-white" data-testid="tdv-faq-target">
                <option value="">All tours</option>
                {(tours || []).map((t) => <option key={t.id} value={t.id}>{t.name}{t.faqs?.length ? ` (${t.faqs.length})` : ""}</option>)}
              </select>
            </div>
            <FaqRows faqs={faqs} setFaqs={setFaqs} />
            <AdminButton variant="ghost" onClick={() => setFaqs([...faqs, { q: "", a: "" }])} data-testid="tdv-faq-add"><Plus className="w-4 h-4" />Add a question</AdminButton>
          </div>
          <KnowledgeDocs home={tour} t={T} base="/tdv/knowledge" />
        </div>
        <div className="space-y-6">
          <Usage a={a} />
          <KeyCard a={a} setA={setA} />
          <Suggestions list={a.chat_suggestions} set={(v) => setA({ ...a, chat_suggestions: v })} />
          <div className="rounded-3xl bg-white border p-6" data-testid="tdv-faq-ideas">
            <p className="font-semibold mb-3">Ideas visitors often ask</p>
            <div className="flex flex-wrap gap-2">{IDEAS.map((q, i) => <button key={q} onClick={() => setFaqs([...faqs, { q, a: "" }])} className="text-left text-xs rounded-full border px-3 py-1.5 hover:border-slate-500" data-testid={`tdv-faq-idea-${i}`}>+ {q}</button>)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
